import { useSelector } from 'react-redux';
import type { RootState } from '../store/redux/store/store';

/**
 * Single source of truth for "may this user manage assets?" on the client.
 *
 * Owner-decided access model:
 *  - `Roles.AssetManagement` stays an ADDITIVE Identity tag stacked on a primary role.
 *    `state.user.isAssetManager` is NOT "holds the tag": the server sets it to the EFFECTIVE
 *    capability, `tag || Hr`. So it is already true for Hr users who hold no tag.
 *  - Hr is an asset manager IMPLICITLY, with full write on every Assets tab and every
 *    asset settings tab. HR is never required to hold the tag.
 *  - Admin is NOT implicitly an asset manager — Admin must be granted the tag like any
 *    other employee. (Admin may still ADMINISTER the tag; see `useCanAdministerAssetRoles`.)
 *  - Operator is out of the asset model entirely.
 *
 * This mirrors the server contract (`IsAssetManager = tag || Hr`). The `|| role === 'Hr'`
 * below is therefore redundant against a fresh server response — it is kept purely as a
 * defensive fallback for a stale/persisted `isAssetManager` snapshot written before the
 * server started folding Hr into the flag.
 */
export const useCanManageAssets = (): boolean => {
  const isAssetManager = useSelector((state: RootState) => state.user.isAssetManager);
  const role = useSelector((state: RootState) => state.user.role);
  return isAssetManager === true || role === 'Hr';
};

/**
 * Who may GRANT/REVOKE the AssetManagement tag. Administering the tag is a separate
 * concern from holding it: both Hr and Admin may administer it, regardless of whether
 * they hold it themselves.
 */
export const useCanAdministerAssetRoles = (): boolean => {
  const role = useSelector((state: RootState) => state.user.role);
  return role === 'Hr' || role === 'Admin';
};

export default useCanManageAssets;
